import { useState, useEffect, useCallback } from 'react';
import {
  Plus, Pencil, Trash2, Loader2, ArrowLeftRight, ChevronLeft, ChevronRight, X,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import api from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';

const CURRENCY_SYMBOLS = { TRY: '₺', USD: '$', EUR: '€' };

const FMT = (n, currency = 'TRY') => {
  const sym = CURRENCY_SYMBOLS[currency] || '₺';
  return `${sym}${new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(n)}`;
};

const TRY_FMT = (n) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(n);

const DATE_FMT = (d) =>
  new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });

const TX_TYPES = ['Borç', 'Tahsilat', 'Ödeme'];

const TX_BADGE = {
  'Borç':     'rose',
  'Tahsilat': 'success',
  'Ödeme':    'warning',
};

const EMPTY_FORM = {
  cari_id: '', type: '', amount: '', currency: 'TRY', exchange_rate: '',
  description: '', transaction_date: new Date().toISOString().split('T')[0],
};

export default function Transactions() {
  const { hasPermission } = useAuth();
  const { toast } = useToast();

  const [txns, setTxns] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  const [filterCari, setFilterCari] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  const [cariList, setCariList] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    api.get('/cari', { params: { limit: 500 } })
      .then(({ data }) => setCariList(data.data))
      .catch(() => {});
  }, []);

  const fetchTxns = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = { page, limit: 15 };
      if (filterCari && filterCari !== 'all') params.cari_id = filterCari;
      if (filterType && filterType !== 'all') params.type = filterType;
      if (filterFrom) params.from = filterFrom;
      if (filterTo) params.to = filterTo;
      const { data } = await api.get('/transactions', { params });
      setTxns(data.data);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch {
      toast({ title: 'Hata', description: 'İşlemler yüklenemedi.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [page, filterCari, filterType, filterFrom, filterTo]); // eslint-disable-line

  useEffect(() => { fetchTxns(); }, [fetchTxns]);
  useEffect(() => { setPage(1); }, [filterCari, filterType, filterFrom, filterTo]);

  const clearFilters = () => {
    setFilterCari('all'); setFilterType('all'); setFilterFrom(''); setFilterTo('');
  };

  const hasActiveFilters = filterCari !== 'all' || filterType !== 'all' || filterFrom || filterTo;

  const openAdd = () => { setEditTarget(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (tx) => {
    setEditTarget(tx);
    setForm({
      cari_id: tx.cari_id,
      type: tx.type,
      amount: tx.amount,
      currency: tx.currency || 'TRY',
      exchange_rate: tx.exchange_rate && parseFloat(tx.exchange_rate) !== 1 ? tx.exchange_rate : '',
      description: tx.description || '',
      transaction_date: tx.transaction_date?.split('T')[0] || new Date().toISOString().split('T')[0],
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const payload = {
        ...form,
        exchange_rate: form.currency === 'TRY' ? 1 : (parseFloat(form.exchange_rate) || 1),
      };
      if (editTarget) {
        await api.put(`/transactions/${editTarget.id}`, payload);
        toast({ title: 'Güncellendi', description: 'İşlem başarıyla güncellendi.', variant: 'success' });
      } else {
        await api.post('/transactions', payload);
        toast({ title: 'Eklendi', description: 'İşlem başarıyla eklendi.', variant: 'success' });
      }
      setDialogOpen(false);
      fetchTxns();
    } catch (err) {
      toast({ title: 'Hata', description: err.response?.data?.error || 'İşlem başarısız.', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await api.delete(`/transactions/${deleteTarget.id}`);
      toast({ title: 'Silindi', description: 'İşlem silindi.', variant: 'success' });
      setDeleteTarget(null);
      fetchTxns();
    } catch (err) {
      toast({ title: 'Hata', description: err.response?.data?.error || 'Silme başarısız.', variant: 'destructive' });
    } finally {
      setIsDeleting(false);
    }
  };

  const tryEquivalent = form.amount && form.currency !== 'TRY' && form.exchange_rate
    ? parseFloat(form.amount) * parseFloat(form.exchange_rate)
    : null;

  return (
    <div className="space-y-5">
      {/* Başlık */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            İşlemler
            <Badge variant="secondary" className="text-xs font-normal">{total} kayıt</Badge>
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Finansal hareketleri takip edin</p>
        </div>
        {hasPermission('add_transaction') && (
          <Button onClick={openAdd} className="gap-2">
            <Plus className="w-4 h-4" /> Yeni İşlem Ekle
          </Button>
        )}
      </div>

      {/* Filtreler */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-36">
              <Label className="text-xs mb-1 block">Cari</Label>
              <Select value={filterCari} onValueChange={setFilterCari}>
                <SelectTrigger><SelectValue placeholder="Tümü" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Cariler</SelectItem>
                  {cariList.map(c => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-36">
              <Label className="text-xs mb-1 block">İşlem Türü</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger><SelectValue placeholder="Tümü" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Türler</SelectItem>
                  {TX_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-36">
              <Label className="text-xs mb-1 block">Başlangıç</Label>
              <Input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} />
            </div>
            <div className="min-w-36">
              <Label className="text-xs mb-1 block">Bitiş</Label>
              <Input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} />
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-400 gap-1">
                <X className="w-3.5 h-3.5" /> Temizle
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tablo */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            </div>
          ) : txns.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-slate-500">
              <ArrowLeftRight className="w-10 h-10 mb-2 opacity-30" />
              <p className="text-sm">İşlem bulunamadı</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tarih</TableHead>
                  <TableHead>Cari</TableHead>
                  <TableHead>Tür</TableHead>
                  <TableHead className="text-right">Tutar</TableHead>
                  <TableHead className="text-right">TRY Karşılığı</TableHead>
                  <TableHead>Açıklama</TableHead>
                  <TableHead className="text-right">İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {txns.map((tx) => {
                  const rate = parseFloat(tx.exchange_rate) || 1;
                  const tryVal = parseFloat(tx.amount) * rate;
                  const isNonTRY = tx.currency && tx.currency !== 'TRY';
                  return (
                    <TableRow key={tx.id}>
                      <TableCell className="text-slate-500 dark:text-slate-400 text-xs whitespace-nowrap">
                        {DATE_FMT(tx.transaction_date)}
                      </TableCell>
                      <TableCell className="font-medium text-slate-900 dark:text-slate-100">{tx.cari_name}</TableCell>
                      <TableCell>
                        <Badge variant={TX_BADGE[tx.type] || 'secondary'}>{tx.type}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold text-slate-800 dark:text-slate-200">
                        {FMT(tx.amount, tx.currency || 'TRY')}
                        {isNonTRY && (
                          <span className="block text-xs text-slate-500 dark:text-slate-500 font-normal">
                            kur: {rate}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-slate-700 dark:text-slate-300 text-sm">
                        {TRY_FMT(tryVal)}
                      </TableCell>
                      <TableCell className="text-slate-500 dark:text-slate-400 text-xs max-w-36 truncate">
                        {tx.description || '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {hasPermission('edit_transaction') && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-blue-400"
                              onClick={() => openEdit(tx)}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {hasPermission('delete_transaction') && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-rose-400"
                              onClick={() => setDeleteTarget(tx)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-800 text-sm text-slate-500 dark:text-slate-400">
            <span>Toplam {total} kayıt</span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-slate-600 dark:text-slate-400">{page} / {totalPages}</span>
              <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Ekle/Düzenle Modalı */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editTarget ? 'İşlemi Düzenle' : 'Yeni İşlem Ekle'}</DialogTitle>
            <DialogDescription>
              {editTarget ? 'İşlem bilgilerini güncelleyin' : 'Bir cariye yeni finansal işlem ekleyin'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label>Cari *</Label>
              <Select value={String(form.cari_id)} onValueChange={v => setForm(f => ({ ...f, cari_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Cari seçin" /></SelectTrigger>
                <SelectContent>
                  {cariList.map(c => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>İşlem Türü *</Label>
                <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seçin" /></SelectTrigger>
                  <SelectContent>
                    {TX_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Tutar *</Label>
                <Input type="number" step="0.01" min="0.01" placeholder="0.00"
                  value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} required />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Para Birimi</Label>
                <Select value={form.currency}
                  onValueChange={v => setForm(f => ({ ...f, currency: v, exchange_rate: v === 'TRY' ? '' : f.exchange_rate }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TRY">₺ Türk Lirası</SelectItem>
                    <SelectItem value="USD">$ Dolar</SelectItem>
                    <SelectItem value="EUR">€ Euro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.currency !== 'TRY' && (
                <div className="space-y-1.5">
                  <Label>1 {form.currency} = ? ₺</Label>
                  <Input type="number" step="0.0001" min="0.0001" placeholder="Kur giriniz"
                    value={form.exchange_rate}
                    onChange={e => setForm(f => ({ ...f, exchange_rate: e.target.value }))} />
                </div>
              )}
            </div>

            {tryEquivalent !== null && (
              <div className="rounded-lg bg-slate-800/60 border border-slate-700 px-3 py-2 text-sm text-slate-300">
                TRY karşılığı: <span className="font-semibold text-blue-400">{TRY_FMT(tryEquivalent)}</span>
              </div>
            )}

            <div className="space-y-1.5">
              <Label>Tarih *</Label>
              <Input type="date" value={form.transaction_date}
                onChange={e => setForm(f => ({ ...f, transaction_date: e.target.value }))} required />
            </div>

            <div className="space-y-1.5">
              <Label>Açıklama</Label>
              <Input placeholder="İsteğe bağlı açıklama..." value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>

            <DialogFooter className="gap-2 mt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>İptal</Button>
              <Button type="submit" disabled={isSaving || !form.cari_id || !form.type || !form.amount}>
                {isSaving ? <><Loader2 className="w-4 h-4 animate-spin" /> Kaydediliyor...</> : 'Kaydet'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Silme Onay */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-rose-400">İşlemi Sil</DialogTitle>
            <DialogDescription>
              Bu işlem kalıcı olarak silinecek. Bu işlem geri alınamaz.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 mt-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>İptal</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sil'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
